import { useI18n } from '@/hooks/useI18n';
import { Eye, Flame, Heart, Play } from 'lucide-react';
import type { ReelResponse } from '@/types/api';
import { formatNumber, formatRelative } from '@/lib/format';
import { proxyImage } from '@/lib/imageProxy';
import { cn } from '@/lib/cn';

interface ReelRowProps {
  reel: ReelResponse;
  onClick: () => void;
}

export function ReelRow({ reel, onClick }: ReelRowProps) {
  const { t, locale } = useI18n();
  const viral = !!reel.viralFlaggedAt;
  const engagement =
    reel.likesCount !== null && reel.viewsCount > 0 ? reel.likesCount / reel.viewsCount : null;

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'group relative grid min-w-0 grid-cols-[70px_minmax(0,1fr)] items-stretch gap-x-3 overflow-hidden rounded-[14px] border bg-bg-1 text-left shadow-card transition-all duration-200 sm:flex sm:h-[124px] sm:gap-3',
        viral
          ? 'border-violet/40 ring-1 ring-violet/15 hover:ring-violet/30'
          : 'surface-card-hover border-line-1',
      )}
    >
      <div className="relative aspect-[9/16] h-[124px] shrink-0 overflow-hidden bg-bg-2 sm:h-full">
        {reel.thumbnailUrl ? (
          <img
            src={proxyImage(reel.thumbnailUrl)}
            alt={reel.caption ?? reel.shortcode}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <PlaceholderThumb seed={reel.shortcode} />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
        <div className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity group-hover:opacity-100">
          <Play className="h-5 w-5 fill-white text-white drop-shadow" />
        </div>
      </div>

      <div className="flex min-w-0 flex-1 flex-col justify-between py-3 pr-3">
        <div>
          {viral && (
            <span className="mb-1.5 inline-flex items-center gap-1 rounded-full bg-violet-soft px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-violet-deep">
              <Flame className="h-2.5 w-2.5" />
              viral
            </span>
          )}
          {reel.caption ? (
            <p className="line-clamp-2 break-words text-[13px] leading-snug text-text-1">
              {reel.caption}
            </p>
          ) : (
            <p className="text-[13px] italic text-text-4">{t('pas de légende.', 'no caption.')}</p>
          )}
        </div>
        <div className="break-words text-[11px] text-text-3">
          {formatRelative(reel.publishedAt)} · {reel.shortcode}
        </div>
      </div>

      <div
        className={cn(
          'col-span-2 grid min-w-0 grid-cols-3 gap-2 border-t px-3 py-2.5 sm:flex sm:w-[100px] sm:shrink-0 sm:flex-col sm:justify-center sm:gap-1.5 sm:border-l sm:border-t-0 sm:py-0',
          viral ? 'border-violet/20' : 'border-line-1',
        )}
      >
        <Metric
          icon={Eye}
          label={t('Vues', 'Views')}
          value={reel.viewsCount}
          accent={viral ? 'violet' : 'neutral'}
        />
        <Metric icon={Heart} label={t('J’aime', 'Likes')} value={reel.likesCount} accent="rose" />
        <div className="text-[11px] text-text-3">
          <span className="mb-1 block text-[10px] sm:sr-only">{t('Engagement', 'Engagement')}</span>
          {engagement !== null
            ? `${(engagement * 100).toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`
            : '—'}{' '}
          <span className="hidden sm:inline">eng</span>
        </div>
      </div>
    </button>
  );
}

function Metric({
  icon: Icon,
  label,
  value,
  accent,
}: {
  icon: typeof Eye;
  label: string;
  value: number | null;
  accent: 'violet' | 'rose' | 'neutral';
}) {
  useI18n();
  const color =
    accent === 'violet' ? 'text-violet-deep' : accent === 'rose' ? 'text-rose' : 'text-text-2';
  return (
    <span className={cn('min-w-0 text-[12px] tabular-nums', color)}>
      <span className="mb-1 block text-[10px] text-text-3 sm:sr-only">{label}</span>
      <span className="flex items-center gap-1">
        <Icon className="h-3 w-3 shrink-0" />
        {value === null ? '—' : formatNumber(value)}
      </span>
    </span>
  );
}

function PlaceholderThumb({ seed }: { seed: string }) {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0;
  const hue = Math.abs(h) % 360;
  return (
    <div
      className="h-full w-full"
      style={{
        background: `linear-gradient(135deg, hsl(${hue} 60% 90%), hsl(${(hue + 60) % 360} 60% 96%))`,
      }}
    />
  );
}
